// İSG Portal — Supplier Panosu + Çok-Firma Özeti (Supplier Dashboard) API çağrıları + tipleri.
// **Taşeron Yetkilisi tarafı** (supplier login §5.4 sonrası — terminal token). İki soru: (1)
// "bu firmada bugün beni ne durduruyor?" → `GET /supplier/dashboard` (tek firma — aktif firma),
// (2) "hangi ana firmamda bugün engel var?" → `GET /supplier/workspaces/summary` (çok firma;
// firma DEĞİŞTİRMEDEN). İkisi de **salt-okuma projeksiyondur**: hiçbir mutation taşımaz.
//
// KAYNAK: api-contract.md §28 (Supplier Panosu + Çok-Firma Özeti). Backend kanonu
// (`backend/src/Isgportal.Permits/Models/SupplierDashboardDtos.cs` +
// `Isgportal.Api/Controllers/SupplierDashboardController.cs`); burada **varsayım YOK** —
// endpoint yolları, istek/yanıt alanları ve kimlik kuralları (Result unwrap `value`; çok-firma
// listesi → `value` + `total`/`filteredTotal`, §3) doğrudan sözleşmeden türetilmiştir.
// Serileştirme camelCase (§1).
//
// **Sayım kuralı TEK yerdedir (§28.1) ve iki uç da onu türetir.** İstemci `blockedToday` /
// `atRiskThisWeek`'i hiçbir alandan (ör. §13/§15'in 30 günlük ön-uyarı `validityStatus`'ü)
// **yeniden hesaplamaz** — sunucudan geleni gösterir. Rozet (§28.4) ile panonun (§28.3) sayacı
// **aynı türetmeden** okunur; istemci ikisini ayrı hesaplarsa özellik değersizleşir.
//
// **Firma/contractor kimliği istemciden ALINMAZ.** İki uç da parametresizdir; kapsam §21 ile
// aynı disiplinle (aktif firmadaki `SupplierUserCompany.ContractorId` bağı) sunucuda çözülür.
//
// **`null` = bilinmiyor, `0` = engel yok (KRİTİK — §28.2).** Çok-firma özetinde bir satırın
// `state !== 'ok'` olması sayaçların `null` olması demektir, **ASLA `0` değil** — `0` "bu
// firmada her şey yolunda" demektir ve bilinmeyeni öyle göstermek bir İSG ürününde kabul
// edilebilir en kötü hata modudur. Bu ayrım burada tip düzeyinde de korunur
// (`SupplierWorkspaceSummaryDto` — `state` daraltılmadan sayaçlar `number` olarak okunamaz).

import { api } from './client';
import type {
  ContractorComplianceDto,
  ContractorStatus,
  HazardClass,
  PersonnelDocumentApprovalStatus,
} from './contractors';
import type { WorkPermitSiteDto } from './permits';

// ============================================================================
// 28.3 GET /supplier/dashboard — tek firma panosu
// ============================================================================

/**
 * Panodaki taşeron özeti — api-contract §28.3 (`SupplierDashboardContractorDto`). `compliance`
 * §13'ün MEVCUT `ContractorComplianceDto`'sudur — **düzleştirilmez**.
 *
 * ⚠️ `contractEnd` burada §13'ün `ContractorDto.contractEnd`'inden (DateOnly, `yyyy-MM-dd`)
 * **FARKLI** olarak DateTime (UTC ISO 8601, §4) serileştirilir — backend
 * `SupplierDashboardContractorDto.ContractEnd: DateTime?`
 * (`backend/src/Isgportal.Permits/Models/SupplierDashboardDtos.cs`) bunu doğrular; istemci
 * gün göstermek isterse değeri kendisi kırpar (contractors.ts'teki `ContractorDto.contractEnd`
 * ile karıştırılmaz).
 */
export interface SupplierDashboardContractorDto {
  uuid: string;
  name: string;
  status: ContractorStatus;
  /** UTC ISO 8601 (§4) — yukarıdaki nota bakınız (§13 `ContractorDto.contractEnd` DEĞİL). */
  contractEnd: string | null;
  compliance: ContractorComplianceDto;
}

/**
 * Panonun sayaçları — api-contract §28.1 (TEK sayım kaynağı) + §28.3. `blockedToday` /
 * `atRiskThisWeek` / `nextCutoffAt` tanımları §28.1'dedir, burada **tekrarlanmaz**.
 *
 * - `pendingApproval` **yalnız personel belgeleridir** (§15.4); `ContractorDocument`'ta (§13.2)
 *   onay kavramı **yoktur** — bu sayaca hiçbir koşulda girmez.
 * - `personnelReady` + `personnelBlocked` paydası §28.1(0)'ın temel kümesidir — taşeronun
 *   **toplam personel mevcudu değildir**; istemci bunu böyle etiketlemek zorundadır.
 * - `personnelBlocked === blockedToday` her zaman (aynı türetme, ayrı bağlam için taşınır) —
 *   istemci ikisini bağımsız hesaplamaz.
 */
export interface SupplierDashboardCountsDto {
  blockedToday: number;
  atRiskThisWeek: number;
  /** UTC ISO 8601; §28.1(4) — geçmişte olabilir, sunucu **kırpmaz**. */
  nextCutoffAt: string | null;
  pendingApproval: number;
  personnelReady: number;
  personnelBlocked: number;
  openPermits: number;
}

/** "Sizden bekleyenler" kalemi türü — api-contract §28.3 (`SupplierActionItemDto.kind`). */
export type SupplierActionItemKind =
  | 'PersonnelDocument'
  | 'ContractorDocument'
  | 'Training'
  | 'PermitApproval';

/**
 * Kalemi kimin düzeltebileceği — api-contract §28.3 NORMATİF eşleme (tahmin değil):
 * `Supplier` → Yetkili düzeltebilir (§15.2 `POST .../personnel/{uuid}/documents` veya §13.6
 * `POST .../contractor/documents`); `Personnel` → **yalnız personelin kendisi** (§17.1
 * `/supplier/me/trainings`) — Yetkili **kapatamaz**; `Expert` → İSG uzmanının işi (§15.4 belge
 * onayı veya §14 izin onayı) — Yetkili yalnız görür. İstemci "Sizden bekleyenler" kutusunu
 * yalnız `blockedBy === 'Supplier'` kalemlerinden kurar.
 */
export type SupplierActionItemBlockedBy = 'Supplier' | 'Personnel' | 'Expert';

/** Kalem önem derecesi — api-contract §28.3; sıralamanın birincil anahtarı (azalan). */
export type SupplierActionItemSeverity = 'Blocking' | 'Warning' | 'Info';

/**
 * "Sizden bekleyenler" kalemi — api-contract §28.3 (`SupplierActionItemDto`). Sözleşme **düz**
 * (flat) bir şema tanımlar — hangi opsiyonel alanın hangi `kind` için dolu geldiği backend
 * türetmesine bağlıdır; istemci `kind`'a göre bir alanın **var olduğunu varsaymaz**, yalnız
 * doluysa kullanır. Sunucuda deterministik sıralı + üst sınırlıdır
 * (`Settings:SupplierDashboard:MaxActionItems`, varsayılan 100) — kırpılmamış gerçek sayı
 * `SupplierDashboardDto.itemsTotal`'dedir. Bu şema §28.7'nin (`GET /supplier/dashboard/items`)
 * kalemiyle **BİREBİR AYNIDIR** — uca özel alan yoktur, aşağıya bkz.
 *
 * `documentType` §27 kayıt kodudur; entity'si `kind`'dan okunur: `PersonnelDocument` → `personnel`,
 * `ContractorDocument` → `company` (aynı kod iki entity'de bulunabilir — `Insurance`). Etiket §27
 * kaydından `(entity, code)` ile çözülür; kayıtta yoksa ham kod.
 */
export interface SupplierActionItemDto {
  kind: SupplierActionItemKind;
  blockedBy: SupplierActionItemBlockedBy;
  severity: SupplierActionItemSeverity;
  /** UTC ISO 8601; yoksa `null`. */
  dueAt: string | null;
  personnelUuid?: string;
  /** "Ad Soyad". */
  personnelName?: string;
  permitUuid?: string;
  permitTitle?: string;
  /** §27 kayıt kodu — entity `kind`'a bağlı (`personnel` / `company`). */
  documentType?: string;
  /** İlgili belge/bağ satırının uuid'si. */
  linkUuid?: string;
  validUntil?: string | null;
  approvalStatus?: PersonnelDocumentApprovalStatus;
  rejectionReason?: string;
}

/**
 * Panodaki yaklaşan/açık izin satırı — api-contract §28.3 (`SupplierUpcomingPermitDto`). Küme
 * §28.1(0)'ın temel kümesidir (yeni bir filtre yok); sunucu **sıralamadan sonra** ilk N'i döner
 * (`Settings:SupplierDashboard:MaxUpcomingPermits`, varsayılan 5).
 *
 * ⛔ Kırpılmamış gerçek sayı **ayrı bir alanda taşınmaz** — `SupplierDashboardCountsDto.
 * openPermits`'tir (aynı temel kümenin boyutu, §28.1(0)). İstemci toplam kopyasını (ör. "3 / 7
 * izin") diziyi **sayarak değil**, `counts.openPermits`'ten kurar
 * (`upcomingPermits.length <= counts.openPermits` her zaman doğrudur).
 *
 * Tekil `siteName` **kullanılmaz** — `sites: WorkPermitSiteDto[]` (§14 Faz 2 çoklu lokasyon;
 * `SupplierPermitDto` ile birebir aynı alan/anlam, supplierPermits.ts).
 */
export interface SupplierUpcomingPermitDto {
  uuid: string;
  permitTypeName: string;
  title: string;
  sites: WorkPermitSiteDto[];
  hazardClass: HazardClass;
  /** UTC ISO 8601 (§4). */
  validityStart: string;
  /** UTC ISO 8601 (§4). */
  validityEnd: string;
  /** Türetilmiş (§14) — temel küme gereği `Approved` | `Active`. */
  status: string;
  personnelCount: number;
  /** §28.1(1) = §17.2 readiness — İKİ boyut BİRDEN (belge VE eğitim). */
  readyPersonnelCount: number;
  /**
   * §17.2'nin **BELGE** boyutunu geçen personel — payda **her zaman** `personnelCount`
   * (§28.3 "Kapı kırılımı"). İznin tipinde istenen personel belge türü yoksa bu boyut
   * trivially geçer → `documentsSatisfiedPersonnelCount === personnelCount` (kenar durum,
   * "belge takılı" OKUNMAZ).
   */
  documentsSatisfiedPersonnelCount: number;
  /**
   * §17.2'nin **EĞİTİM** boyutunu geçen personel — payda **her zaman** `personnelCount`.
   * İznin gerekli eğitimi yoksa bu boyut trivially geçer →
   * `trainingsCompletePersonnelCount === personnelCount` (kenar durum, "eğitim takılı"
   * OKUNMAZ).
   *
   * ⛔ **İki alt sayı TOPLANMAZ.** `documentsSatisfiedPersonnelCount +
   * trainingsCompletePersonnelCount` hiçbir şeye eşit değildir — aynı kişi iki boyutta da
   * eksik olabilir, kümeler örtüşür (§28.3). `readyPersonnelCount` ikisini BİRDEN geçen
   * kişileri sayar; üç sayı aynı kişileri farklı kesitlerden sayar, "toplam hazır" diye
   * gösterilmez.
   */
  trainingsCompletePersonnelCount: number;
}

/**
 * 7 günlük risk serisinin tekil günü — api-contract §28.1(5)/§28.3 (`SupplierRiskDayDto`).
 *
 * - **Değişmez:** `leadTimeCount + lapseCount === personnelCount` (her gün, her yanıt).
 * - `documentTypes` dal B'nin tanımı gereği **asla** `expiryRule = NoExpiry`
 *   olan bir tür içermez (bu türlerin `validUntil`'i kapıda denetlenmez, §17.2, §28.1(3)).
 * - `permits[].personnelCount` izinler arasında **tekilleştirilmemiştir** — istemci bu diziyi
 *   **toplamaz** (yalnız "hangi iş yüzünden" kopyasını kurar); günün gerçek kişi sayısı yine
 *   `personnelCount`'tur.
 * - `cutoffAt` OLAY tabanlıdır, kişi bazında **değildir**: `personnelCount = 0` iken de dolu
 *   olabilir ("o gün yeni kimse engellenmiyor ama zaten engelli biri için son yükleme anı var")
 *   — istemci bu durumda cutoff'u **gizlemez**.
 */
export interface SupplierRiskDayDto {
  /** `'YYYY-MM-DD'` — yanıtın `timezone`'undaki TAKVİM GÜNÜ (UTC anı DEĞİL); istemci üretmez. */
  date: string;
  /** O gün engellenecek TEKİL kişi sayısı; `0` olabilir → istemci '—' basar. */
  personnelCount: number;
  /** Bunlardan Dal A (izin başlıyor) — §28.1(3). */
  leadTimeCount: number;
  /** Bunlardan Dal B (belge doluyor) — §28.1(3). */
  lapseCount: number;
  /** UTC ISO 8601; OLAY tabanlı (§28.1(5)) — `personnelCount = 0` iken de dolu olabilir. */
  cutoffAt: string | null;
  /** O gün dolan, kapıya sayan İSTENEN türler (§27 `personnel` kayıt kodları; `[]` olabilir). */
  documentTypes: string[];
  /** O güne katkı veren izinler (ada göre sıralı; `[]` olabilir). */
  permits: SupplierRiskPermitRefDto[];
}

/**
 * Bir günün seri satırına katkı veren izin referansı — api-contract §28.1(5)
 * (`SupplierRiskPermitRefDto`). `personnelCount` **yalnız bu iznin o güne katkısıdır** —
 * ⛔ izinler arasında toplanamaz (`Σ permits[].personnelCount >= personnelCount` olabilir).
 */
export interface SupplierRiskPermitRefDto {
  uuid: string;
  title: string;
  /** UTC ISO 8601 (§4). */
  validityStart: string;
  /** İzin O GÜN mü başlıyor ("başlıyor" vs "devam ediyor" kopyası için). */
  startsOnThisDay: boolean;
  personnelCount: number;
}

/**
 * `riskDays` — 7 günlük **YOĞUN (dense)** seri, api-contract §28.1(5). Uzunluk **HER ZAMAN 7**
 * (engel üretmeyen gün de `personnelCount = 0` ile döner, omit edilmez); bugün serinin
 * **dışındadır** (bugünün cevabı `counts.blockedToday`'dir). Sıra tarih artan
 * (bugün+1 → bugün+7).
 *
 * Tuple tipi bu uzunluk garantisini **derleme zamanında zorlar** — 6 ya da 8 elemanlı bir dizi
 * atanmaya çalışılırsa tip hatası verir; `riskDays[6]` her zaman güvenle 7. günü okur
 * (`riskDays[7]` derlemede reddedilir). Bu, `Σ riskDays[].personnelCount ===
 * counts.atRiskThisWeek` değişmezini istemci tarafında da somutlaştırır.
 */
export type SupplierRiskDaySeries = readonly [
  SupplierRiskDayDto,
  SupplierRiskDayDto,
  SupplierRiskDayDto,
  SupplierRiskDayDto,
  SupplierRiskDayDto,
  SupplierRiskDayDto,
  SupplierRiskDayDto,
];

/**
 * Supplier panosu — api-contract §28.3 (`SupplierDashboardDto`, `GET /supplier/dashboard`
 * yanıtı). Sayfalama parametresi **yoktur**; istek hiçbir parametre almaz — `items`'in
 * sayfalanmış + kategoriye göre süzülmüş hâli için **§28.7 `GET /supplier/dashboard/items`**'e
 * (aşağıda) bakınız; gömülü `items` yalnız ilk boyama içindir (ekstra gidiş-dönüş olmasın diye)
 * ve §28.7'nin `category=all&page=1&pageSize=<MaxActionItems>` isteğiyle **BİREBİR AYNIDIR**
 * (aynı sıra, aynı kalemler, aynı uzunluk — ayrışırlarsa backend hatalıdır).
 */
export interface SupplierDashboardDto {
  /** UTC ISO 8601 (§4) — sunucunun hesap anı. */
  asOf: string;
  /** IANA — istemci gün aritmetiğini **BUNUNLA** yapar (cihaz zon'u DEĞİL; §28 girişi). */
  timezone: string;
  contractor: SupplierDashboardContractorDto;
  counts: SupplierDashboardCountsDto;
  /** Sunucuda sıralı + üst sınırlı (varsayılan 100); §28.7 = sayfalı hâli. */
  items: SupplierActionItemDto[];
  /**
   * Sınır uygulanmadan ÖNCEKİ gerçek kalem sayısı — kapsam uygulanmış, kategori filtresi
   * uygulanMAmış. §28.7'nin `total`'ı ile **AYNI sayıdır** (aynı türetme, aynı an); §28.7'nin
   * `filteredTotal`'ı (kategoriye göre süzülmüş) ile **KARIŞTIRILMAZ**.
   */
  itemsTotal: number;
  upcomingPermits: SupplierUpcomingPermitDto[];
  /** §28.1(5) — 7 günlük YOĞUN seri; uzunluk HER ZAMAN 7. */
  riskDays: SupplierRiskDaySeries;
}

/**
 * `GET /supplier/dashboard` (`get`) — aktif firmadaki pano projeksiyonu (§28.3). İstek **hiçbir
 * parametre almaz**; firma/contractor kimliği aktif firmadaki `SupplierUserCompany.ContractorId`
 * bağından sunucuda çözülür (§21 kapsam disiplini ile birebir). Bağ yoksa →
 * `404 CONTRACTOR_NOT_FOUND`; aktif firmaya atama yoksa → `403 COMPANY_NOT_ACCESSIBLE`; grant
 * yok (Taşeron **Personeli**) veya transit `workspace_select` token (rol claim'i yok) →
 * `403 FORBIDDEN`.
 */
export function getSupplierDashboard() {
  return api.get<SupplierDashboardDto>('supplier/dashboard');
}

// ============================================================================
// 28.7 GET /supplier/dashboard/items — sayfalanmış + kategoriye göre süzülmüş "Eksikler"
// ============================================================================

/**
 * "Eksikler" listesi süzgeci — api-contract §28.7 `category` (tek değerli, küçük harf).
 * Varsayılan `all`. Değerler **TAM PARTİSYONDUR** (`document` ∪ `training` ∪
 * `permit-approval` = `all`, ikili kesişimsiz) — eşleme SUNUCUDADIR, istemci bir `kind`
 * listesi göndermez:
 *
 * | `category` | Kapsanan `kind` |
 * | --- | --- |
 * | `all` | dördü birden |
 * | `document` | `PersonnelDocument`, `ContractorDocument` |
 * | `training` | `Training` |
 * | `permit-approval` | `PermitApproval` |
 *
 * Bugünkü web istemcisi yalnız `all`/`document`/`training` sekmelerini gösterir — bu bir
 * **istemci tercihidir** (bkz. `MissingItemsCard.tsx`), sözleşme dördünü de tanımlar.
 * Tanınmayan bir değer sunucuda varsayılana (`all`) çözülür — `400` **dönmez** (§13.1/§20.3
 * idiomu; §28.7'nin bilinçli yön farkı: buradaki varsayılan §20.3'ün aksine **geniştir**).
 */
export type SupplierActionItemCategory = 'all' | 'document' | 'training' | 'permit-approval';

/**
 * `GET /supplier/dashboard/items` liste/sayfalama parametreleri — api-contract §28.7 + §3
 * paging. `page`/`pageSize` varsayılanı §3'ündür (`1`/`50`); tavan
 * `Settings:SupplierDashboard:MaxActionItems` (§28.3'ün gömülü üst sınırıyla **AYNI** knob,
 * §3'ün 200'ü DEĞİL) — aşımda `400` **değil**, sunucu clamp'ler.
 */
export interface SupplierDashboardItemListParams {
  category?: SupplierActionItemCategory;
  page?: number;
  pageSize?: number;
}

/**
 * `GET /supplier/dashboard/items` (`get`) — §28.3'ün `items`'inin **sayfalanmış +
 * `category`'ye göre süzülmüş** hâli (api-contract §28.7). Kalem şekli `SupplierActionItemDto`
 * ile **BİREBİR AYNIDIR** — bu uca özel alan yoktur. Sıra §28.3'ün normatif sırasıyla **AYNI**
 * (burada yeniden tanımlanmaz/tekrarlanmaz) ve **tamdır** (sayfalar arası tekrar/atlama yok).
 *
 * Firma/contractor kimliği **GÖNDERİLMEZ** — §28.3 ile aynı disiplinle sunucuda çözülür (aktif
 * firmadaki `SupplierUserCompany.ContractorId` bağı). Zarf `value` + `total` (kapsam
 * uygulanmış, `category` uygulanMAmış — §28.3'ün `itemsTotal`'ı ile **AYNI** sayı) +
 * `filteredTotal` (`category` uygulandıktan **sonra**, sayfalamadan **önce**) döner —
 * `api.list` bu üçünü ayrıştırır.
 *
 * ⛔ **Bir sekmenin sayısı YALNIZ o isteğin `filteredTotal`'ından okunur** — `total`/
 * `value.length` bir sekme etiketinde **gösterilemez** (`total` filtresiz toplamdır,
 * `value.length` sayfa boyudur). `asOf`/`timezone` bu uçta **taşınmaz** — istemci bunları
 * `GET /supplier/dashboard`'dan okur (pano zaten sayaçlar için yüklenmiştir).
 *
 * Hata kodları §28.3 ile **BİREBİR AYNI** (yeni kod yok): `401 UNAUTHORIZED`;
 * `403 FORBIDDEN` (grant yok / transit token); `403 COMPANY_NOT_ACCESSIBLE`
 * (supplier değil / aktif firma yok / atama iptal); `404 CONTRACTOR_NOT_FOUND` (bağ yok).
 */
export function listSupplierDashboardItems(params: SupplierDashboardItemListParams = {}) {
  return api.list<SupplierActionItemDto>('supplier/dashboard/items', {
    searchParams: {
      category: params.category,
      page: params.page,
      pageSize: params.pageSize,
    },
  });
}

// ============================================================================
// 28.4 GET /supplier/workspaces/summary — çok-firma özeti
// ============================================================================

/** Çok-firma özeti satır durumu — api-contract §28.2. */
export type SupplierWorkspaceState = 'ok' | 'unauthorized' | 'unavailable';

/**
 * `SupplierWorkspaceSummaryDto`'nun state'ten bağımsız ortak alanları.
 *
 * `asOf` + `timezone` §28.4'ün normatif notuyla **her satırda** taşınır ve **tüm satırlarda
 * birebir aynı** değeri alır (yanıt başına sabit — zarfta değil satırda, çünkü §1 zarfına alan
 * eklenmez). **`state !== 'ok'` (`unauthorized`/`unavailable`) satırlarda da DOLUDURLAR, ASLA
 * `null` değil** — sayaçların `null` kuralı (aşağıdaki iki alt tip) bu ikisine uygulanmaz; bu
 * yüzden ikisi ortak temelde, `Ok`/`Unresolved` ayrımının DIŞINDA tanımlıdır.
 *
 * ⚠️ `timezone` **firmanın** zon'u DEĞİLDİR — **çağıranın**kidir (principal `timezone` claim'i
 * / `DefaultTimezone`); §28.3'ün `SupplierDashboardDto.timezone`'uyla birebir aynıdır. İstemci
 * `nextCutoffAt`'i CİHAZ zon'unda biçimlendirmez — satırın kendi `timezone`'unu kullanır (aynı
 * kural aslında her satırda aynı sonucu verir, ama okuma satırdan yapılır).
 */
interface SupplierWorkspaceSummaryBaseDto {
  /**
   * `/auth/switch-company`'nin **ALDIĞI** anahtar (§5.6) — §4a'nın "sıralı iç `Id` istemciye
   * sızmaz" kuralına bilinçli, auth yüzeyine mahsus bir istisnadır (§28.4 notu); `companyUuid`
   * ile **değiştirilemez** (var olan uç uuid kabul etmez).
   */
  companyId: number;
  companyUuid: string;
  companyName: string;
  /** Token'daki aktif firma mı. `true` iken bile `state !== 'ok'` OLABİLİR (§28.4 notu). */
  isActive: boolean;
  /** UTC ISO 8601 (§4) — sunucunun hesap anı; HER SATIRDA aynı, `state`'ten BAĞIMSIZ dolu. */
  asOf: string;
  /** IANA — ÇAĞIRANIN zon'u (firmanın değil); HER SATIRDA aynı, ASLA `null`. */
  timezone: string;
}

/**
 * `state = 'ok'` — sayaçlar gerçek sayılardır; `0` "bu firmada engel yok" demektir (`null`
 * DEĞİLDİR).
 */
export interface SupplierWorkspaceSummaryOkDto extends SupplierWorkspaceSummaryBaseDto {
  state: 'ok';
  /** §28.1(2). */
  blockedToday: number;
  /** §28.1(3) — dal-nötr toplam (kırılım bu uçta taşınmaz; kırılım için §28.3 `riskDays`). */
  atRiskThisWeek: number;
  /** UTC ISO 8601; §28.1(4) — olay yoksa `null`, `state = 'ok'` iken de `null` OLABİLİR. */
  nextCutoffAt: string | null;
}

/**
 * `state ∈ { 'unauthorized', 'unavailable' }` — çözümleme başarısız/yetkisiz; sayaçlar HER
 * ZAMAN `null`, **ASLA `0`** (§28.2). `unauthorized` → çağıranın o firmada taşeron kapsamı yok
 * (tekil değer; iç ayrım yok) — istemci "Bu firmada taşeron yetkiniz yok" der, otomatik yeniden
 * denemez. `unavailable` → geçici çözümleme hatası (shard/timeout/iptal) — istemci yeniden
 * deneyebilir.
 */
export interface SupplierWorkspaceSummaryUnresolvedDto extends SupplierWorkspaceSummaryBaseDto {
  state: 'unauthorized' | 'unavailable';
  blockedToday: null;
  atRiskThisWeek: null;
  nextCutoffAt: null;
}

/**
 * Çok-firma özeti satırı — api-contract §28.4 (`SupplierWorkspaceSummaryDto`). **`state`'e göre
 * daraltılmış birleşim (discriminated union)**, kontrattaki `number | null` optional-alan
 * şemasının bilerek sıkılaştırılmış hâli: `state === 'ok'` kontrol edilmeden sayaçlar `number`
 * olarak okunamaz, derleyici her erişimde `null` olasılığını zorlar. Amaç plan S26'nın "Bilinen
 * tuzaklar" maddesini tip düzeyinde imkânsız kılmak — bilinmeyeni ("unauthorized"/"unavailable")
 * "yolunda" (`0`) göstermek bir İSG ürününde kabul edilebilir en kötü hata modudur.
 */
export type SupplierWorkspaceSummaryDto =
  | SupplierWorkspaceSummaryOkDto
  | SupplierWorkspaceSummaryUnresolvedDto;

/**
 * `GET /supplier/workspaces/summary` (`get`) — çağıranın erişebildiği **TÜM** firmalar için
 * §28.1 sayaçlarının özeti (switcher rozeti; §28.4). **Terminal token gerekir** — transit
 * `workspace_select` token'ı rol claim'i taşımadığından `403 FORBIDDEN` alır (firma seçim
 * ekranında **gösterilemez**, §28 girişi).
 *
 * İstek **hiçbir parametre almaz** (sayfalama/filtre yok — §28.4); firma kümesi token + taze
 * `SupplierUserCompany` kesişiminden gelir, istemci `companyIds` **göndermez/gönderemez**.
 * Yanıt `Cache-Control: no-store` taşır (principal'a özel güvenlik durumu — paylaşımlı/proxy
 * önbelleğe düşmez); `ETag`/`If-None-Match` **yoktur**.
 *
 * Zarf `value` + `total` (çözülen erişilebilir firma sayısı — fan-out tavanı
 * `Settings:SupplierDashboard:MaxWorkspaceFanout` aşılırsa `value.length < total`) +
 * `filteredTotal` (`=== total`; filtre yok) döner — `api.list` bu üçünü ayrıştırır.
 *
 * Hata kodları (§28.4): `401 UNAUTHORIZED`; `403 FORBIDDEN` (grant yok / transit token);
 * `403 COMPANY_NOT_ACCESSIBLE` (supplier değil / aktif firma yok / **aktif** firma ataması
 * iptal — bu bir satır durumu değil, zarf hatasıdır). Diğer firmaların çözülememesi zarfı
 * **düşürmez**; ilgili satır `state !== 'ok'` ile döner (§28.2).
 */
export function getSupplierWorkspacesSummary() {
  return api.list<SupplierWorkspaceSummaryDto>('supplier/workspaces/summary', {});
}
